import { getDefaultI18n } from './chunk-2UGAFJLN.js';
import { Button } from './chunk-PLP233UC.js';
import { cn } from './chunk-FFX3LZW7.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { toErrorResponse, retryHelpsReader } from '@authe/fe-commons/errors';
import { messageForError } from '@authe/fe-commons/i18n';

var SIZES = {
  sm: "py-6",
  md: "py-10",
  lg: "py-16"
};
function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  size = "md"
}) {
  return (
    // `status`, not `alert`: an empty result is a fact about the data, not a
    // failure, and interrupting the reader for "No rows match these filters"
    // would be a false alarm. But it still has to be HEARD — a filter that
    // empties a table changes the whole body, and the panel that explains why
    // is the only thing left on the page — so the polite live region a
    // `status` carries is exactly the right strength. (`ErrorState` is the one
    // that assertively announces, because it is a failed read.)
    /* @__PURE__ */ jsxs(
      "div",
      {
        role: "status",
        className: cn(
          "flex flex-col items-center justify-center text-center",
          SIZES[size],
          className
        ),
        children: [
          icon && /* @__PURE__ */ jsx(
            "div",
            {
              "aria-hidden": true,
              className: "mb-3 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground",
              children: icon
            }
          ),
          /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold", children: title }),
          description && /* @__PURE__ */ jsx("p", { className: "mt-1 max-w-sm text-sm text-muted-foreground", children: description }),
          action && /* @__PURE__ */ jsx("div", { className: "mt-4", children: action })
        ]
      }
    )
  );
}
var SIZES2 = {
  sm: "py-6",
  md: "py-10",
  lg: "py-16"
};
function ErrorState({
  error,
  i18n,
  title,
  detail,
  onRetry,
  retryLabel = "Try again",
  alwaysAllowRetry = false,
  className,
  size = "md"
}) {
  const response = toErrorResponse(error);
  const resolved = messageForError(response.error, i18n ?? getDefaultI18n() ?? void 0);
  const retryable = alwaysAllowRetry || retryHelpsReader(response);
  return (
    // `role="alert"` (the same semantics the `Alert` primitive carries, without
    // its bordered-box styling, which is wrong for a panel that REPLACES a
    // table body rather than sitting above one). A failed read swaps the table
    // out for this panel, and with no live region a screen-reader user was told
    // nothing and carried on reading a table that had left the page. One
    // change, every one of this component's call sites.
    /* @__PURE__ */ jsxs(
      "div",
      {
        role: "alert",
        className: cn("flex flex-col items-center justify-center text-center", SIZES2[size], className),
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              "aria-hidden": true,
              className: "mb-3 flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive",
              children: /* @__PURE__ */ jsx(AlertTriangle, { className: "size-6" })
            }
          ),
          /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold", children: title ?? resolved.description }),
          (detail ?? resolved.userAction) && /* @__PURE__ */ jsx("p", { className: "mt-1 max-w-sm text-sm text-muted-foreground", children: detail ?? resolved.userAction }),
          response.error.correlation_id && /* @__PURE__ */ jsxs("p", { className: "mt-2 font-mono text-xs text-muted-foreground/70", children: [
            "ref: ",
            response.error.correlation_id
          ] }),
          onRetry && retryable && /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", className: "mt-4 gap-2", onClick: onRetry, children: [
            /* @__PURE__ */ jsx(RotateCw, { className: "size-3.5" }),
            retryLabel
          ] })
        ]
      }
    )
  );
}

export { EmptyState, ErrorState };
//# sourceMappingURL=chunk-ENZ247DM.js.map
//# sourceMappingURL=chunk-ENZ247DM.js.map